/**
 * Fur & Far harness/collar size calculator.
 * Uses the Size & Fit Guide measurements (neck, chest, weight) against
 * this product's own size options. Default bands follow typical walking
 * harness/collar ranges and can be replaced per product via data-chart.
 */
(function (root) {
  const ORDER = ['xxs', 'xs', 's', 'm', 'l', 'xl', '2xl', '3xl'];

  const HARNESS_BANDS = {
    xxs: { chestMin: 22, chestMax: 30, neckMin: 16, neckMax: 22, weightMin: 1, weightMax: 3 },
    xs: { chestMin: 28, chestMax: 38, neckMin: 20, neckMax: 28, weightMin: 1.5, weightMax: 5 },
    s: { chestMin: 36, chestMax: 48, neckMin: 26, neckMax: 34, weightMin: 4, weightMax: 10 },
    m: { chestMin: 46, chestMax: 62, neckMin: 32, neckMax: 42, weightMin: 9, weightMax: 20 },
    l: { chestMin: 58, chestMax: 78, neckMin: 40, neckMax: 52, weightMin: 18, weightMax: 32 },
    xl: { chestMin: 74, chestMax: 98, neckMin: 48, neckMax: 64, weightMin: 28, weightMax: 45 },
    '2xl': { chestMin: 90, chestMax: 120, neckMin: 58, neckMax: 78, weightMin: 40, weightMax: 60 },
  };

  const COLLAR_BANDS = {
    xxs: { neckMin: 16, neckMax: 21, chestMin: 22, chestMax: 30, weightMin: 1, weightMax: 3 },
    xs: { neckMin: 20, neckMax: 26, chestMin: 28, chestMax: 38, weightMin: 1.5, weightMax: 5 },
    s: { neckMin: 25, neckMax: 32, chestMin: 36, chestMax: 48, weightMin: 4, weightMax: 10 },
    m: { neckMin: 31, neckMax: 40, chestMin: 46, chestMax: 62, weightMin: 9, weightMax: 20 },
    l: { neckMin: 38, neckMax: 50, chestMin: 58, chestMax: 78, weightMin: 18, weightMax: 32 },
    xl: { neckMin: 46, neckMax: 62, chestMin: 74, chestMax: 98, weightMin: 28, weightMax: 45 },
    '2xl': { neckMin: 58, neckMax: 76, chestMin: 90, chestMax: 120, weightMin: 40, weightMax: 60 },
  };

  function canonicalSize(value) {
    const raw = String(value || '').toLowerCase();
    const v = raw
      .replace(/luminous/g, ' ')
      .replace(/\bcode\b/g, ' ')
      .replace(/\bupgrade\b/g, ' ')
      .replace(/\bsize\b/g, ' ')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!v) return '';
    if (/\b(3xl|xxx l|xxx-large|triple extra large)\b/.test(v) || v === '3xl') return '3xl';
    if (/\b(2xl|xxl|xx-large|double extra large)\b/.test(v) || v === '2xl' || v === 'xxl') return '2xl';
    if (/\b(extra\s*large|x-?large|xlarge)\b/.test(v) || v === 'xl') return 'xl';
    if (/\b(extra\s*small|x-?small|xsmall)\b/.test(v) || v === 'xs') return 'xs';
    if (/\b(xxs|2xs|extra extra small)\b/.test(v) || v === 'xxs') return 'xxs';
    if (/\blarge\b/.test(v) || v === 'l') return 'l';
    if (/\bmedium\b/.test(v) || v === 'm') return 'm';
    if (/\bsmall\b/.test(v) || v === 's') return 's';
    const token = v.split(' ').find((part) => ORDER.includes(part));
    return token || '';
  }

  function isLetterSize(value) {
    return canonicalSize(value) !== '';
  }

  function isWearableSizedProduct({ type, title, tags, sizeValues }) {
    const hay = `${type || ''} ${title || ''} ${(tags || []).join(' ')}`.toLowerCase();
    const wearable = /\bharness|\bcollar|\bvest|\bapparel|\bclothing/.test(hay);
    const letterSizes = (sizeValues || []).filter(isLetterSize);
    return Boolean(wearable && letterSizes.length > 0);
  }

  function calculatorMode({ type, title, tags }) {
    const hay = `${type || ''} ${title || ''} ${(tags || []).join(' ')}`.toLowerCase();
    if (/\bcollar/.test(hay) && !/\bharness|\bvest/.test(hay)) return 'collar';
    return 'harness';
  }

  function parseNumber(value) {
    if (value == null || value === '') return null;
    const number = Number(String(value).replace(',', '.'));
    if (!Number.isFinite(number) || number <= 0) return null;
    return number;
  }

  function inRange(value, min, max) {
    if (value == null || !Number.isFinite(min) || !Number.isFinite(max)) return null;
    return value >= min && value <= max;
  }

  function buildChart(mode, sizeValues, customChart) {
    if (customChart && Array.isArray(customChart.sizes) && customChart.sizes.length) {
      return customChart.sizes.map((row) => ({
        id: canonicalSize(row.id || row.label) || String(row.label || '').toLowerCase(),
        label: row.label,
        chestMin: Number(row.chestMin),
        chestMax: Number(row.chestMax),
        neckMin: Number(row.neckMin),
        neckMax: Number(row.neckMax),
        weightMin: Number(row.weightMin),
        weightMax: Number(row.weightMax),
      })).filter((row) => row.label);
    }

    const bands = mode === 'collar' ? COLLAR_BANDS : HARNESS_BANDS;
    const used = new Set();
    const chart = [];
    (sizeValues || []).forEach((label) => {
      const id = canonicalSize(label);
      if (!id || used.has(id) || !bands[id]) return;
      used.add(id);
      chart.push({ id, label, ...bands[id] });
    });
    chart.sort((a, b) => ORDER.indexOf(a.id) - ORDER.indexOf(b.id));
    return chart;
  }

  function recommendSize({ mode, chart, chest, neck, weight }) {
    const primary = mode === 'collar' ? neck : chest;
    const primaryKey = mode === 'collar' ? 'neck' : 'chest';
    if (primary == null) {
      return {
        status: 'need-primary',
        message: mode === 'collar'
          ? 'Enter a neck measurement to get a collar size.'
          : 'Enter a chest / girth measurement to get a harness size.',
      };
    }
    if (!chart.length) {
      return { status: 'no-chart', message: 'This product does not have wearable sizes to calculate.' };
    }

    const scored = chart.map((row) => {
      const primaryFit = inRange(primary, row[`${primaryKey}Min`], row[`${primaryKey}Max`]);
      const neckFit = neck == null ? null : inRange(neck, row.neckMin, row.neckMax);
      const chestFit = chest == null ? null : inRange(chest, row.chestMin, row.chestMax);
      const weightFit = weight == null ? null : inRange(weight, row.weightMin, row.weightMax);
      const midpoint = (row[`${primaryKey}Min`] + row[`${primaryKey}Max`]) / 2;
      const distance = Math.abs(primary - midpoint);
      let score = 0;
      if (primaryFit) score += 8;
      if (neckFit) score += 2;
      if (chestFit) score += mode === 'collar' ? 2 : 0;
      if (weightFit) score += 1;
      if (primaryFit === false) score -= 6;
      return { row, primaryFit, neckFit, weightFit, distance, score };
    });

    const fits = scored.filter((item) => item.primaryFit).sort((a, b) => b.score - a.score || a.distance - b.distance);
    if (fits.length === 1) {
      const pick = fits[0];
      const extras = [];
      if (pick.neckFit === false) extras.push('neck is outside this size’s usual range');
      if (pick.weightFit === false) extras.push('weight is outside this size’s usual range');
      return {
        status: 'match',
        size: pick.row.label,
        canonical: pick.row.id,
        message: extras.length
          ? `Suggested size: ${pick.row.label}. Check the size photos — ${extras.join(' and ')}.`
          : `Suggested size: ${pick.row.label}. Confirm against the size photos, then choose that option above.`,
      };
    }
    if (fits.length > 1) {
      const larger = fits.slice().sort((a, b) => ORDER.indexOf(b.row.id) - ORDER.indexOf(a.row.id))[0];
      const names = fits.map((item) => item.row.label).join(' and ');
      return {
        status: 'between',
        size: larger.row.label,
        canonical: larger.row.id,
        message: `This sits between ${names}. ${larger.row.label} is usually the more comfortable choice unless they have a very slim build. Confirm on the size photos.`,
      };
    }

    const smallest = chart[0];
    const largest = chart[chart.length - 1];
    if (primary < smallest[`${primaryKey}Min`]) {
      return {
        status: 'too-small',
        message: `These measurements look smaller than ${smallest.label}, the smallest size on this product. Check the size photos or ask us with the measurements.`,
      };
    }
    if (primary > largest[`${primaryKey}Max`]) {
      return {
        status: 'too-large',
        message: `These measurements look larger than ${largest.label}, the largest size on this product. Check the size photos or ask us with the measurements.`,
      };
    }

    const nearest = scored.slice().sort((a, b) => a.distance - b.distance)[0];
    return {
      status: 'nearest',
      size: nearest.row.label,
      canonical: nearest.row.id,
      message: `Closest listed size: ${nearest.row.label}. This is near a size boundary — confirm on the size photos before you buy.`,
    };
  }

  function bind(rootEl) {
    const form = rootEl.querySelector('[data-size-calc-form]');
    const result = rootEl.querySelector('[data-size-calc-result]');
    const apply = rootEl.querySelector('[data-size-calc-apply]');
    if (!form || !result) return;

    const mode = rootEl.dataset.mode || 'harness';
    const sizeValues = (rootEl.dataset.sizes || '').split('|').map((value) => value.trim()).filter(Boolean);
    let customChart = null;
    if (rootEl.dataset.chart) {
      try {
        customChart = JSON.parse(rootEl.dataset.chart);
      } catch (error) {
        customChart = null;
      }
    }
    const chart = buildChart(mode, sizeValues, customChart);

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const chest = parseNumber(form.elements.chest && form.elements.chest.value);
      const neck = parseNumber(form.elements.neck && form.elements.neck.value);
      const weight = parseNumber(form.elements.weight && form.elements.weight.value);
      const advice = recommendSize({ mode, chart, chest, neck, weight });
      result.hidden = false;
      result.dataset.status = advice.status;
      result.textContent = advice.message;
      if (apply) {
        apply.hidden = !advice.size;
        apply.dataset.size = advice.size || '';
      }
    });

    if (apply) {
      apply.addEventListener('click', () => {
        const label = apply.dataset.size;
        if (!label) return;
        const page = document.querySelector('[data-product-page]');
        const picker = page && page.querySelector('[data-variant-picker]');
        const sizeIndex = picker && picker.getAttribute('data-size-option-index');
        const wanted = canonicalSize(label);
        const sizeButton = [...(picker ? picker.querySelectorAll('[data-option-value]') : [])].find((button) => {
          if (sizeIndex != null && button.dataset.optionIndex !== sizeIndex) return false;
          if (button.getAttribute('aria-disabled') === 'true') return false;
          return canonicalSize(button.dataset.value) === wanted || button.dataset.value === label;
        });
        if (sizeButton) {
          sizeButton.click();
          return;
        }
        const select = page && page.querySelector('[data-variant-select]');
        if (!select) return;
        const selected = {};
        if (picker) {
          picker.querySelectorAll('[data-option-value][aria-pressed="true"]').forEach((button) => {
            if (sizeIndex != null && button.dataset.optionIndex === sizeIndex) return;
            selected[button.dataset.optionIndex] = button.dataset.value;
          });
        }
        const match = [...select.options].find((option) => {
          const sizeValue = sizeIndex != null ? option.getAttribute(`data-option-${sizeIndex}`) : option.textContent;
          if (canonicalSize(sizeValue) !== wanted && sizeValue !== label) return false;
          return Object.keys(selected).every((key) => option.getAttribute(`data-option-${key}`) === selected[key]);
        });
        if (!match) return;
        select.value = match.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }

  function init(scope) {
    const rootEls = [...(scope || document).querySelectorAll('[data-size-calculator]')];
    rootEls.forEach(bind);
  }

  root.FurFarSizeCalculator = {
    ORDER,
    canonicalSize,
    isLetterSize,
    isWearableSizedProduct,
    calculatorMode,
    parseNumber,
    buildChart,
    recommendSize,
    init,
  };

  document.addEventListener('DOMContentLoaded', () => init());
})(typeof window !== 'undefined' ? window : globalThis);
